import React, { useState, useEffect, useContext } from 'react';
import { StorageContext } from '../../contexts/StorageContext';
import { ProjectContext } from '../../contexts/ProjectContext';
import PromptPanel from '../PromptPanel';
import ResponsePanel from '../ResponsePanel';
import FileSelector from '../FileSelector';
import UpdateTypeSelector from '../UpdateTypeSelector';
import CodePreview from '../CodePreview';
import PromptGenerator from '../../services/PromptGenerator';
import ResponseProcessor from '../../services/ResponseProcessor';
import UpdateProtocol from '../../services/UpdateProtocol';
import './RefinementPhase.css';

/**
 * Refinement phase component
 * Handles making targeted modifications to specific files
 */
function RefinementPhase() {
  const { storage } = useContext(StorageContext);
  const { project, updateProject } = useContext(ProjectContext);
  const [files, setFiles] = useState([]);
  const [selectedFile, setSelectedFile] = useState(null);
  const [selectedFileContent, setSelectedFileContent] = useState('');
  const [updateType, setUpdateType] = useState('fix');
  const [issueDescription, setIssueDescription] = useState('');
  const [prompt, setPrompt] = useState('');
  const [isGeneratingPrompt, setIsGeneratingPrompt] = useState(false);
  const [isProcessingResponse, setIsProcessingResponse] = useState(false);
  const [processingResults, setProcessingResults] = useState(null);
  const [updateHistory, setUpdateHistory] = useState([]);
  const [promptGenerator] = useState(new PromptGenerator());
  const [responseProcessor] = useState(new ResponseProcessor(storage));
  const [updateProtocol] = useState(new UpdateProtocol(storage));

  // Update type options
  const updateTypes = [
    { id: 'fix', label: 'Bug Fix', description: 'Fix a bug or issue in the code' },
    { id: 'performance', label: 'Performance', description: 'Improve code performance' },
    { id: 'refactor', label: 'Refactor', description: 'Improve code structure without changing functionality' },
    { id: 'feature', label: 'Feature', description: 'Add a new feature or enhancement' },
    { id: 'security', label: 'Security', description: 'Address security vulnerabilities' }
  ];

  // Load files
  useEffect(() => {
    const loadFiles = async () => {
      if (!project) return;
      
      try {
        const projectFiles = await storage.getFilesByProject(project.id);
        setFiles(projectFiles);
      } catch (error) {
        console.error('Error loading files:', error);
      }
    };
    
    loadFiles();
  }, [project, storage]);

  // Load file content when selected
  useEffect(() => {
    const loadFileContent = async () => {
      if (!selectedFile) {
        setSelectedFileContent('');
        return;
      }
      
      try {
        const fileContent = await storage.getFileContent(selectedFile.id);
        if (fileContent) {
          setSelectedFileContent(fileContent.content);
        } else {
          setSelectedFileContent('');
        }
      } catch (error) {
        console.error('Error loading file content:', error);
        setSelectedFileContent('');
      }
    };
    
    loadFileContent();
  }, [selectedFile, storage]);

  // Handle file selection
  const handleFileChange = (fileId) => {
    const file = files.find(f => f.id === fileId);
    setSelectedFile(file || null);
    setIssueDescription('');
    setProcessingResults(null);
  };

  // Handle update type change
  const handleUpdateTypeChange = (type) => {
    setUpdateType(type);
  };

  // Handle issue description change
  const handleIssueDescriptionChange = (e) => {
    setIssueDescription(e.target.value);
  };

  // Generate prompt
  const handleGeneratePrompt = async () => {
    if (!selectedFile) {
      alert('Please select a file to refine');
      return;
    }
    
    if (!issueDescription.trim()) {
      alert('Please describe the issue or enhancement needed');
      return;
    }
    
    try {
      setIsGeneratingPrompt(true);
      
      const generatedPrompt = await promptGenerator.generateRefinementPrompt(
        selectedFile,
        updateType,
        issueDescription,
        selectedFileContent
      );
      
      setPrompt(generatedPrompt);
    } catch (error) {
      console.error('Error generating prompt:', error);
      alert('Failed to generate prompt');
    } finally {
      setIsGeneratingPrompt(false);
    }
  };

  // Process AI response
  const handleProcessResponse = async (response) => {
    try {
      setIsProcessingResponse(true);
      
      const result = await responseProcessor.processResponse(
        response,
        'REFINEMENT',
        project.id
      );
      
      if (result.success) {
        // Apply updates using the update protocol
        const updateResult = await updateProtocol.applyUpdate(
          project.id,
          result.updateOperation
        );
        
        if (updateResult.success) {
          // Update the file content display
          const updatedFileContent = await storage.getFileContent(selectedFile.id);
          setSelectedFileContent(updatedFileContent.content);
          
          // Add to update history
          const newUpdate = {
            id: crypto.randomUUID(),
            timestamp: Date.now(),
            filePath: selectedFile.path,
            updateType,
            description: issueDescription,
            operations: updateResult.operations || []
          };
          
          setUpdateHistory(prev => [newUpdate, ...prev]);
          
          // Store update results
          setProcessingResults(updateResult);
          
          // Clear issue description
          setIssueDescription('');
          
          return { success: true };
        } else {
          return { 
            success: false,
            error: updateResult.error || 'Failed to apply updates'
          };
        }
      } else {
        return { 
          success: false,
          error: result.error || 'Failed to process response'
        };
      }
    } catch (error) {
      console.error('Error processing response:', error);
      return { 
        success: false,
        error: 'Error processing response: ' + error.message 
      };
    } finally {
      setIsProcessingResponse(false);
    }
  };

  // Mark refinement phase complete
  const handleCompletePhase = async () => {
    try {
      // Update project phase
      const updatedProject = {
        ...project,
        refinementComplete: true,
        updated: Date.now()
      };
      
      await storage.updateProject(updatedProject);
      updateProject(updatedProject);
    } catch (error) {
      console.error('Error completing phase:', error);
      alert('Failed to complete refinement phase');
    }
  };

  return (
    <div className="refinement-phase">
      <div className="phase-description">
        <h2>Refinement Phase</h2>
        <p>
          In this phase, you'll make targeted improvements to specific files.
          Select a file, describe the changes needed, and the AI will provide precise updates.
        </p>
      </div>
      
      <div className="refinement-config">
        <div className="file-selection">
          <h3>Select File to Refine</h3>
          <FileSelector 
            files={files}
            selectedFile={selectedFile?.id}
            onFileChange={handleFileChange}
          />
        </div>
        
        <div className="update-config">
          <h3>Define Refinement</h3>
          
          <div className="update-type">
            <label>Update Type:</label>
            <UpdateTypeSelector 
              types={updateTypes}
              selectedType={updateType}
              onTypeChange={handleUpdateTypeChange}
            />
          </div>
          
          <div className="issue-description">
            <label>Description:</label>
            <textarea 
              value={issueDescription}
              onChange={handleIssueDescriptionChange}
              placeholder="Describe the issue or enhancement needed..."
              disabled={!selectedFile}
            />
          </div>
        </div>
      </div>
      
      {selectedFile && (
        <div className="code-preview-container">
          <h3>File Preview: {selectedFile.path}</h3>
          <CodePreview 
            content={selectedFileContent}
            language={selectedFile.language}
            maxHeight={400}
          />
        </div>
      )}
      
      {updateHistory.length > 0 && (
        <div className="update-history">
          <h3>Update History</h3>
          <div className="history-list">
            {updateHistory.map(update => (
              <div key={update.id} className="history-item">
                <div className="history-header">
                  <span className="history-file">{update.filePath}</span>
                  <span className="history-time">{new Date(update.timestamp).toLocaleString()}</span>
                </div>
                <div className="history-type">
                  {updateTypes.find(t => t.id === update.updateType)?.label || update.updateType}
                </div>
                <div className="history-description">{update.description}</div>
              </div>
            ))}
          </div>
        </div>
      )}
      
      <div className="ai-interaction">
        <PromptPanel 
          prompt={prompt}
          onGeneratePrompt={handleGeneratePrompt}
          isLoading={isGeneratingPrompt}
          title="Refinement Prompt"
        />
        
        <ResponsePanel 
          onProcessResponse={handleProcessResponse}
          isProcessing={isProcessingResponse}
          processingResults={processingResults}
          title="Refinement Response"
        />
      </div>
      
      <div className="phase-completion">
        <button 
          className="complete-phase-btn"
          onClick={handleCompletePhase}
        >
          Complete Refinement Phase
        </button>
        <p className="completion-note">
          Click this button when you're satisfied with all refinements and ready to move to the Finishing phase.
        </p>
      </div>
    </div>
  );
}

export default RefinementPhase;
